import { useNotification } from "Hooks";
import { useCallback, useEffect, useRef, useState } from "react";

const OAUTH_CALLBACK_CODE_KEY = "oauth-callback-code";
const OAUTH_CALLBACK_TIMESTAMP_KEY = "oauth-callback-timestamp";
const DEFAULT_POPUP_FEATURES = "popup=yes,width=600,height=640";

interface IUseOAuthPopupOptions {
  onCode: (code: string) => Promise<void>;
  onDenied?: () => void;
  cancelledMessage?: string;
  blockedMessage?: string;
}

function useOAuthPopup({
  onCode,
  onDenied,
  cancelledMessage = "Authentication was cancelled",
  blockedMessage = "Please allow popups for this site to continue",
}: IUseOAuthPopupOptions) {
  const [isOAuthOpen, setIsOAuthOpen] = useState(false);
  const [code, setCode] = useState<string | null>(null);
  const codeAttemptRef = useRef<string | null>(null);
  const closeCheckerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const cleanupListenersRef = useRef<(() => void) | null>(null);
  /** True once the flow finished (code, denied, error, or intentional close). */
  const settledRef = useRef(false);
  const { showErrorNotification } = useNotification();

  const resetPopupState = useCallback((cleanLocalStorage = false) => {
    if (cleanupListenersRef.current) {
      cleanupListenersRef.current();
      cleanupListenersRef.current = null;
    }
    if (closeCheckerRef.current) {
      clearInterval(closeCheckerRef.current);
      closeCheckerRef.current = null;
    }
    if (cleanLocalStorage) {
      localStorage.removeItem(OAUTH_CALLBACK_CODE_KEY);
      localStorage.removeItem(OAUTH_CALLBACK_TIMESTAMP_KEY);
    }
    setIsOAuthOpen(false);
  }, []);

  const settle = useCallback(
    (cleanLocalStorage = false) => {
      settledRef.current = true;
      resetPopupState(cleanLocalStorage);
    },
    [resetPopupState],
  );

  const attachPopupListeners = useCallback(
    (win: Window) => {
      const handleOAuthCode = (oauthCode: string) => {
        settle(true);
        setCode(oauthCode);
        if (!win.closed) {
          win.close();
        }
      };

      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) {
          return;
        }
        if (event.data?.type === "oauth-callback-denied") {
          settle(true);
          onDenied?.();
          showErrorNotification(cancelledMessage);
          if (!win.closed) {
            win.close();
          }
          return;
        }
        if (event.data?.type === "oauth-callback" && event.data.code) {
          handleOAuthCode(event.data.code);
        }
      };

      const handleStorage = (event: StorageEvent) => {
        if (event.key === OAUTH_CALLBACK_CODE_KEY && event.newValue) {
          handleOAuthCode(event.newValue);
        }
      };

      window.addEventListener("message", handleMessage);
      window.addEventListener("storage", handleStorage);

      cleanupListenersRef.current = () => {
        window.removeEventListener("message", handleMessage);
        window.removeEventListener("storage", handleStorage);
      };

      closeCheckerRef.current = setInterval(() => {
        if (win.closed && !settledRef.current) {
          settle(false);
          onDenied?.();
        }
      }, 500);
    },
    [cancelledMessage, onDenied, settle, showErrorNotification],
  );

  /**
   * Opens a blank popup synchronously (must be called from a user gesture),
   * then navigates it to the URL returned by `fetchAuthUrl`.
   * Returns false if the popup was blocked or closed early; otherwise true.
   * Re-throws errors from `fetchAuthUrl` after closing the blank popup.
   */
  const startOAuth = useCallback(
    async (
      fetchAuthUrl: () => Promise<string>,
      windowName = "auth",
    ): Promise<boolean> => {
      const popup = window.open("about:blank", windowName, DEFAULT_POPUP_FEATURES);

      if (!popup) {
        showErrorNotification(blockedMessage);
        return false;
      }

      settledRef.current = false;
      setIsOAuthOpen(true);
      attachPopupListeners(popup);

      try {
        const url = await fetchAuthUrl();
        if (popup.closed) {
          if (!settledRef.current) {
            settle(false);
            onDenied?.();
          }
          return false;
        }
        popup.location.href = url;
        return true;
      } catch (error) {
        settle(false);
        if (!popup.closed) {
          popup.close();
        }
        throw error;
      }
    },
    [
      attachPopupListeners,
      blockedMessage,
      onDenied,
      settle,
      showErrorNotification,
    ],
  );

  useEffect(() => {
    if (!code || codeAttemptRef.current === code) {
      return;
    }

    codeAttemptRef.current = code;
    onCode(code).finally(() => {
      setCode(null);
      codeAttemptRef.current = null;
    });
  }, [code, onCode]);

  useEffect(() => {
    const storedTimestamp = localStorage.getItem(OAUTH_CALLBACK_TIMESTAMP_KEY);
    if (!storedTimestamp) {
      return;
    }

    const age = Date.now() - parseInt(storedTimestamp, 10);
    if (age > 5000) {
      localStorage.removeItem(OAUTH_CALLBACK_CODE_KEY);
      localStorage.removeItem(OAUTH_CALLBACK_TIMESTAMP_KEY);
    }
  }, []);

  useEffect(() => {
    return () => {
      settledRef.current = true;
      resetPopupState(false);
    };
  }, [resetPopupState]);

  return {
    startOAuth,
    isOAuthOpen,
  };
}

export default useOAuthPopup;
